// BrokerPortal.js
import { formatDisplayName } from "../utils/displayName";
// Frosted cards + debounced search + loading state + last-updated
import { useEffect, useMemo, useState } from "react";
import BrokerDashboardLayout from "../components/BrokerDashboardLayout";
import BrokerMonthlyBar from "../components/BrokerMonthlyBar";
import BrokerTracker from "../components/BrokerTracker";
import DonutStatusChart from "../components/DonutStatusChart";
import BrokerKPICards from "../components/BrokerKPICards";
import BrokerTailSearch from "../components/BrokerTailSearch";
import BrokerNotifications from "../components/BrokerNotifications";
import { supabase } from "../supabaseClient";
import { Bell, RefreshCw } from "lucide-react";
import PortalFeedback from "../components/PortalFeedback";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";
import { PROJECT_STATUSES, normalizeProjectStatus } from "../utils/projectStatus";
import { traceAsync } from "../utils/perfTrace";

const DASHBOARD_TIMEOUT_MS = 20000;
const emptyStatusCounts = () =>
  PROJECT_STATUSES.reduce((counts, status) => ({ ...counts, [status]: 0 }), {});
const emptyDashboardCardCounts = () => ({
  Requested: 0,
  Scheduled: 0,
  Production: 0,
  Delivered: 0,
});

// ----- time-range helpers (outside component is fine) -----
const RANGE_TO_DAYS = {
  "30d": 30,
  "60d": 60,
  "90d": 90,
  all: null,
};
function sinceDateFromRange(rangeKey) {
  const days = RANGE_TO_DAYS[rangeKey];
  if (!days) return null; // "all"
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

export default function BrokerPortal() {
  console.log("🚀 [BrokerPortal] mounted");

  // -----------------------------
  // Personalized welcome
  // ----------------------------
  const [displayName, setDisplayName] = useState("Broker");
  const [brokerAccountId, setBrokerAccountId] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadDisplayName = async (user) => {
      if (!mounted || !user) return;

      const { data } = await supabase
        .from("broker_profiles")
        .select("first_name")
        .eq("user_id", user.id)
        .maybeSingle();

      const first =
        data?.first_name?.trim?.() ||
        user.user_metadata?.first_name?.trim?.() ||
        user.user_metadata?.firstName?.trim?.();

      setDisplayName(first || user.email || "Broker");
    };

    (async () => {
      const { data } = await supabase.auth.getUser();
      await loadDisplayName(data?.user);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_evt, session) => {
      await loadDisplayName(session?.user);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // -----------------------------
  // Data state
  // -----------------------------
  const [timeRange, setTimeRange] = useState("30d"); //  default range

  const [projects, setProjects] = useState([]);
  const [projectCounts, setProjectCounts] = useState(() => emptyStatusCounts());
  const [projectCardCounts, setProjectCardCounts] = useState(() => emptyDashboardCardCounts());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dashboardError, setDashboardError] = useState("");

  

  // -----------------------------
  // Fetch dashboard data (once per range change)
  // -----------------------------
  const refetchDashboardData = async () => {
    setLoading(true);
    setDashboardError("");
    try {
      const { data: auth } = await traceAsync(
        "dashboard.broker.getUser",
        () => withTimeout(
          supabase.auth.getUser(),
          DASHBOARD_TIMEOUT_MS,
          "Dashboard session check timed out"
        ),
        {}
      );
      const userId = auth?.user?.id;
      if (!userId) {
        setProjects([]);
        setProjectCounts(emptyStatusCounts());
        setProjectCardCounts(emptyDashboardCardCounts());
        setBrokerAccountId("");
        setLastUpdated(new Date());
        return;
      }

      const { data: membership, error: membershipError } = await traceAsync(
        "dashboard.broker.membership",
        () => withTimeout(
          supabase
            .from("broker_account_members")
            .select("broker_account_id, role")
            .eq("user_id", userId)
            .order("role", { ascending: false })
            .limit(1)
            .maybeSingle(),
          DASHBOARD_TIMEOUT_MS,
          "Broker account lookup timed out"
        ),
        {}
      );

      if (membershipError || !membership?.broker_account_id) {
        if (membershipError) console.error("❌ Broker membership error:", membershipError);
        setProjects([]);
        setProjectCounts(emptyStatusCounts());
        setProjectCardCounts(emptyDashboardCardCounts());
        setBrokerAccountId("");
        setLastUpdated(new Date());
        return;
      }

      setBrokerAccountId(membership.broker_account_id);

      // include broker_intake.created_at for range filtering
      const TRACKER_SELECT =
        "id,tail_number,current_status,priority,assigned_pilot,expected_datetime,timezone,updated_at,broker_intake!inner(created_at,services,broker_account_id)";
      const { data: trackerData, error: trackerError } = await traceAsync(
        "dashboard.broker.projects",
        () => withTimeout(
          supabase
            .from("project_status")
            .select(TRACKER_SELECT)
            .eq("broker_intake.broker_account_id", membership.broker_account_id),
          DASHBOARD_TIMEOUT_MS,
          "Project dashboard lookup timed out"
        ),
        {}
      );

      if (trackerError) {
        console.error("❌ Tracker error:", trackerError);
        setProjects([]);
        setProjectCounts(emptyStatusCounts());
        setProjectCardCounts(emptyDashboardCardCounts());
        setLastUpdated(new Date());
        return;
      }

      setProjects(trackerData || []);

      // filter rows by selected time range
      const since = sinceDateFromRange(timeRange);
      const rowsInRange = (trackerData || []).filter((r) => {
        const ts = r?.broker_intake?.created_at;
        if (!ts) return false;
        if (!since) return true; // "all"
        return new Date(ts) >= since;
      });

      // build counts by normalized status
      const counts = emptyStatusCounts();
      rowsInRange.forEach((r) => {
        const normalized = normalizeProjectStatus(r?.current_status);
        if (counts[normalized] !== undefined) counts[normalized] += 1;
      });

      const cardCounts = { ...emptyDashboardCardCounts(), Production: counts.Production, Delivered: counts.Delivered };
      (trackerData || []).forEach((r) => {
        const normalized = normalizeProjectStatus(r?.current_status);
        if (normalized === "Requested" || normalized === "Scheduled") {
          cardCounts[normalized] += 1;
        }
      });

      setProjectCounts(counts);
      setProjectCardCounts(cardCounts);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("❌ Broker dashboard load failed:", error);
      setProjects([]);
      setProjectCounts(emptyStatusCounts());
      setProjectCardCounts(emptyDashboardCardCounts());
      setLastUpdated(new Date());
      setDashboardError(
        isTimeoutError(error)
          ? "Dashboard took too long to load. Refresh the page or sign out and back in if this browser has been open for a while."
          : "Dashboard could not load. Refresh the page and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetchDashboardData();
  }, [timeRange]);

  // -----------------------------
  // Frosted panel style
  // -----------------------------
  const PANEL =
    "rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

  const formattedUpdatedAt = useMemo(() => {
    if (!lastUpdated) return "";
    return lastUpdated.toLocaleString();
  }, [lastUpdated]);

  return (
    <BrokerDashboardLayout>
  <div className="space-y-8 text-[#1A1A1A]">
        {dashboardError && <PortalFeedback type="error">{dashboardError}</PortalFeedback>}
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              Welcome back, {formatDisplayName(displayName)}!
            </h1>
            {formattedUpdatedAt && (
              <p className="text-xs text-black/60 mt-1">Last updated: {formattedUpdatedAt}</p>
            )}
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
  <BrokerTailSearch className="w-full md:w-80" />
  <BrokerNotifications panelClass={PANEL} />
  <button
    onClick={refetchDashboardData}
    className={`${PANEL} p-2 hover:bg-white/20 transition inline-flex items-center gap-2`}
    aria-label="Refresh dashboard"
    title="Refresh"
  >
    <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
  </button>
</div>
        </div>

        {/* GRID (top row) */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left: Donut */}
          <div className={`${PANEL} p-5`}>
            <div className={loading ? "opacity-50" : ""}>
              <DonutStatusChart
                counts={projectCounts}
                activeRange={timeRange}
                onRangeChange={setTimeRange}
              />
            </div>
          </div>

          {/* Right: KPIs + short bar chart */}
          <div className="flex flex-col gap-6">
            <div className="p-0">
              <BrokerKPICards counts={projectCardCounts} loading={loading} />
            </div>

            <div className={`${PANEL} p-5`}>
              <h2 className="text-lg font-semibold">Requests by Month</h2>
              <BrokerMonthlyBar brokerAccountId={brokerAccountId} heightClass="h-52 md:h-52" />
            </div>
          </div>
        </div>

        {/* Full-width Tracker */}
        <div className="px-0">
  <BrokerTracker
    projects={projects}
    loading={loading}
    limitDays={30}
    maxVisibleRows={5}
    enableSorting
  />
</div>
      </div>
    </BrokerDashboardLayout>
  );
}
